import * as React from 'react';
import Footer from '../../components/Footer';
import Layout from '../../components/Layout';
import { graphql } from 'gatsby';
import Section from '../../components/shared/Section';
import { Image, YoastMeta } from '../../types';
import Hero from '../../components/shared/Hero';
import s from './ArticleTemplate.module.scss';
import Text from '../../components/shared/Text';
import TextBlock, { Props as TextProps } from './Text';
import HeadingBlock, { Props as HeadingProps } from './Heading';
import ImageBlock, { Props as ImageProps } from './Image';
import NewsBlock, { Props as NewsProps } from './News';
import TableBlock, { Props as TableProps } from './Table';
import { News as AllWpNewsType } from '../../types';
import {
  Vertical,
  Horizontal,
} from '../swace/gatsby-background-image/GatsbyBackgroundImage';
import ShareGroup from '../shared/ShareGroup';

interface SiteMeta {
  name: string;
  description: string;
  url: string;
}

export type BlockProps = {
  _blocks: Props[];
  internal: {
    type: string;
  };
  id: string;
  allWpNews: AllWpNewsType;
} & TextProps &
  HeadingProps &
  ImageProps &
  NewsProps &
  TableProps;

export enum ArticleType {
  news,
  person,
  article,
}

interface ArticleNews {
  newsCategory;
  title: string;
  ingress: string;
}
interface ArticlePerson {
  jobTitle: string;
  email: string;
  name: string;
  phone: string;
  location: string;
}
interface Article {
  id: string;
  path: string;
  acf: {
    image: Image;
    heroImage?: Image;
    imagePositioning: {
      vertical: Vertical;
      horizontal: Horizontal;
    };
    blocks: BlockProps[];
  } & ArticleNews &
    ArticlePerson;
  yoast_meta: YoastMeta;
}

interface Props {
  articleType: ArticleType;
  article: Article;
  siteMeta: SiteMeta;
  yoastMeta?: YoastMeta;
  allWpNews?: AllWpNewsType;
}

const ArticleBlock = (props: BlockProps) => {
  switch (props.internal && props.internal.type) {
    case 'WordPressAcf_text':
      return <TextBlock {...props} />;

    case 'WordPressAcf_heading':
      return <HeadingBlock {...props} />;

    case 'WordPressAcf_image':
      return <ImageBlock {...props} />;

    case 'WordPressAcf_news':
      return <NewsBlock {...props} />;

    case 'WordPressAcf_table':
      return <TableBlock {...props} />;

    default:
      return <div />;
  }
};

const ArticleIntro = (props: {
  articleType: ArticleType;
  article: Article;
}) => {
  switch (props.articleType) {
    case ArticleType.news: {
      const { title, ingress } = props.article.acf;
      return (
        <>
          <div className={s.title}>
            <Text h3>{title}</Text>
          </div>
          <div className={s.ingress}>
            <Text ingress>{ingress}</Text>
          </div>
        </>
      );
    }
    case ArticleType.person: {
      const { jobTitle, email, phone, name, location } = props.article.acf;

      return (
        <>
          <div className={s.title}>
            <Text h3>{name}</Text>
          </div>
          <div className={s.ingress}>
            <Text h5>{jobTitle}</Text>
            <Text ingress>{location}</Text>
            <Text ingress>{email}</Text>
            <Text ingress>{phone}</Text>
          </div>
        </>
      );
    }
    case ArticleType.article:
    default:
      return null;
  }
};

export class ArticleTemplate extends React.Component<Props> {
  render() {
    const { article, siteMeta, yoastMeta, articleType, allWpNews } = this.props;
    const { blocks, image, imagePositioning, heroImage } = article.acf;
    const linkToThisPage =
      articleType === ArticleType.news
        ? `${process.env.GATSBY_FRONTEND_BASE_URL}${article.path}`
        : null;

    return (
      <Layout siteMeta={siteMeta} yoastMeta={yoastMeta} disableBackground>
        <Hero
          background_image={image || heroImage}
          imagePositioning={imagePositioning}
        />
        {articleType === ArticleType.news && (
          <ShareGroup link={linkToThisPage} text={article.acf.title} desktop />
        )}
        <Section slimTop>
          <div className="row">
            <div className="col-md-12 col-lg-10 offset-lg-1">
              <ArticleIntro article={article} articleType={articleType} />
              {blocks &&
                blocks.map(block => (
                  <ArticleBlock
                    {...block}
                    allWpNews={allWpNews}
                    key={block.id}
                  />
                ))}
              {articleType === ArticleType.news && (
                <NewsBlock
                  allWpNews={allWpNews}
                  preview_number="3"
                  button_type="action"
                  button_text="Read more"
                />
              )}
            </div>
          </div>
          {articleType === ArticleType.news && (
            <ShareGroup
              link={linkToThisPage}
              text={article.acf.title}
              desktop={false}
            />
          )}
        </Section>
        <Footer />
      </Layout>
    );
  }
}

export const query = graphql`
  fragment NewsFragment on wordpress__wp_news {
    path
    id
    acf {
      image: preview_image {
        localFile {
          childImageSharp {
            fluid(maxWidth: 1200, quality: 90) {
              ...GatsbyImageSharpFluid_withWebp_noBase64
            }
          }
        }
      }
      imagePositioning: image_positioning {
        vertical: vertical_adjustment
        horizontal: horizontal_adjustment
      }
      title
      newsCategory: news_category
      ingress
      blocks: article_news {
        __typename
        ...Article_HeadingBlockFragment
        ...Article_TextBlockFragment
        ...Article_ImageBlockFragment
        ...Article_NewsBlockFragment
      }
    }
    yoastMeta: yoast_meta {
      yoast_wpseo_title
      yoast_wpseo_metadesc
      yoast_wpseo_canonical
      yoast_wpseo_facebook_image {
        source_url
      }
    }
  }
  fragment AllWordpressWpNews on Query {
    allWordpressWpNews(filter: { title: { ne: "SWACE_PLACEHOLDER" } }) {
      edges {
        node {
          id
          slug
          acf {
            title
            newsCategory: news_category
            previewImage: preview_image {
              localFile {
                childImageSharp {
                  fluid(maxWidth: 1200, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp_noBase64
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  fragment PersonFragment on wordpress__wp_person {
    path
    link
    id
    acf {
      image {
        localFile {
          childImageSharp {
            fluid(maxWidth: 1200, quality: 90) {
              ...GatsbyImageSharpFluid_withWebp_noBase64
            }
          }
        }
      }
      imagePositioning: image_positioning {
        vertical: vertical_adjustment
        horizontal: horizontal_adjustment
      }
      jobTitle: title
      email
      name
      phone
      location
      blocks: article_person {
        __typename
        ...Article_HeadingBlockFragment
        ...Article_TextBlockFragment
        ...Article_ImageBlockFragment
      }
    }
  }
`;

export default ArticleTemplate;
